import { InferType, array, number, object, string } from 'yup';

type TranslationFn = (key: string, options?: Record<string, unknown>) => string;

/**
 * Shared yup schema for company forms (CompanyDetails, EditCompanyDialog, CreateCompanyDialog).
 * CreateCompanyDialog extends this with additional Stripe fields.
 */
/**
 * Entitlement apps are company-level boolean grants rather than per-user seat products.
 * On the backend they are stored as an App with `seats = NULL` and granted to every user
 * in the company. The portal renders them as on/off switches, never as seat inputs. When a
 * new entitlement product ships (e.g. 'mia', 'ace'), add it here and to `appDisplayNames`.
 */
export const ENTITLEMENT_APPS = ['presentations', 'canonry'] as const;

export const isEntitlementApp = (clientId: string): boolean =>
  (ENTITLEMENT_APPS as readonly string[]).includes(clientId);

/**
 * Entitlement apps that nothing auto-grants: they are not included on company create and were
 * never backfilled, so an absent grant is a deliberate "not opted in" rather than missing data.
 * `useHasApp` must fail closed for these -- see the empty-`apps` fail-open it documents.
 * Canonry/AI Visibility works this way on purpose: a DIQAdmin grants the entitlement, then the
 * customer's own Admin completes setup.
 */
export const OPT_IN_ENTITLEMENT_APPS = ['canonry'] as const;

export const isOptInEntitlementApp = (clientId: string): boolean =>
  (OPT_IN_ENTITLEMENT_APPS as readonly string[]).includes(clientId);

/** Seat apps that appear in the seat-count editor (excludes 'core' and entitlement apps). */
export const SEAT_APP_OPTIONS = ['hero', 'polaris'];

export const buildCompanySchema = (t: TranslationFn, tCommon: TranslationFn) =>
  object({
    companyName: string()
      .required(t('templates:required-template', { noun: t('company:name') }))
      .max(256)
      .trim(),
    currency: string().required(t('templates:required-template', { noun: t('company:setting-form-currency') })),
    // Seat apps only — entitlement apps live in `entitlements` and are not seat-validated.
    apps: array(
      object({
        clientId: string()
          .default('')
          .required(t('templates:required-template', { noun: t('company:app') })),
        seats: number()
          .typeError(tCommon('valid-number'))
          .required(t('templates:required-template', { noun: tCommon('seats') }))
          .min(1, tCommon('greater-than', { number: 0 }))
          .max(1000000, tCommon('less-than', { number: '1,000,000' }))
          .integer(tCommon('integer', { noun: tCommon('seats') })),
      })
    )
      .required()
      // Entitlement apps are sold independently of seat apps, so a company may exist
      // with zero seat apps as long as an entitlement is enabled (backend always adds
      // 'core' on create).
      .test(
        'app-or-entitlement',
        t('company:apps-or-entitlement-required', {
          defaultValue: 'Add at least one app with seats, or enable an entitlement.',
        }),
        function (apps) {
          const entitlements = (this.parent as { entitlements?: string[] }).entitlements ?? [];
          return (apps?.length ?? 0) > 0 || entitlements.length > 0;
        }
      ),
    // Enabled company-level entitlement apps (e.g. 'presentations'), stored by clientId.
    entitlements: array(string().required()).default([]),
    verticals: array(string().required())
      .required()
      .min(1, tCommon('at-least-one', { noun: t('company:verticals') })),
  });

export type CompanyFormValues = InferType<ReturnType<typeof buildCompanySchema>>;

export const appDisplayNames: Record<string, string> = {
  hero: 'Hero',
  polaris: 'Journeys',
  presentations: 'Presentations',
  canonry: 'AI Visibility',
};

export const getAppDisplayName = (clientId: string): string => appDisplayNames[clientId] ?? clientId;
